/**
 * Import Graph Node Components
 * Issue #500: Custom node rendering for import graph within ReactFlowRenderer.
 */

import { Handle, Position, type NodeProps } from '@xyflow/react';

const hiddenHandleStyle = { opacity: 0, width: 6, height: 6 };
import { ROLE_COLORS } from './importGraphFlowConverter';

/**
 * Custom node for import graph files.
 * Shows file name, path, role color, and badges for entry/barrel/circular.
 */
function ImportGraphNodeComponent({ data, id }: NodeProps) {
  const importedBy = (data.importedBy as string[]) || [];
  const imports = (data.imports as string[]) || [];
  const role = (data.role as string) || 'unknown';
  const filePath = (data.filePath as string) || '';
  const name = (data.name as string) || (data.label as string) || '';
  const isEntryPoint = data.isEntryPoint as boolean;
  const isBarrel = data.isBarrel as boolean;
  const hasCircularDep = data.hasCircularDep as boolean;
  const isRoot = data.isRoot as boolean;
  // Phase 2: expansion mode callbacks
  const onExpand = data.onExpand as ((nodeId: string) => void) | undefined;
  const isExpanded = data.isExpanded as boolean | undefined;

  const nodeSize = Math.min(200, Math.max(120, 100 + importedBy.length * 10));
  const roleColor = ROLE_COLORS[role] || ROLE_COLORS.unknown;

  // Issue #612: role-based fill colors — solid blue for root, light tint for others.
  // Appending '26' (hex) ≈ 15% opacity gives a clearly visible tint without obscuring text.
  const bgColor = isRoot ? '#3b82f6' : roleColor + '26';

  return (
    <div
      className={`px-3 py-2 rounded-lg border-2 shadow-md transition-all ${
        isRoot ? 'border-blue-600' :
        hasCircularDep ? 'border-red-500 ring-2 ring-red-200' : 'border-gray-300'
      }`}
      style={{
        backgroundColor: bgColor,
        color: isRoot ? 'white' : undefined,
        minWidth: nodeSize,
        borderLeftWidth: '4px',
        borderLeftColor: isRoot ? '#1d4ed8' : roleColor,
      }}
    >
      <Handle type="target" position={Position.Top} style={hiddenHandleStyle} />
      <Handle type="target" position={Position.Bottom} style={hiddenHandleStyle} />
      <Handle type="target" position={Position.Left} style={hiddenHandleStyle} />
      <Handle type="target" position={Position.Right} style={hiddenHandleStyle} />
      <Handle type="source" position={Position.Top} style={hiddenHandleStyle} />
      <Handle type="source" position={Position.Bottom} style={hiddenHandleStyle} />
      <Handle type="source" position={Position.Left} style={hiddenHandleStyle} />
      <Handle type="source" position={Position.Right} style={hiddenHandleStyle} />

      <div className="font-medium text-sm truncate" title={filePath}>
        {name}
      </div>
      <div
        className="text-xs truncate"
        style={{ color: isRoot ? 'rgba(255,255,255,0.8)' : undefined }}
        title={filePath}
      >
        <span className={isRoot ? '' : 'text-gray-500'}>{filePath}</span>
      </div>
      <div className="flex gap-2 mt-1 text-xs flex-wrap">
        <span
          className="px-1.5 py-0.5 rounded"
          style={{ backgroundColor: isRoot ? 'rgba(255,255,255,0.2)' : '#f3f4f6' }}
          title="Files that import this"
        >
          ← {importedBy.length}
        </span>
        <span
          className="px-1.5 py-0.5 rounded"
          style={{ backgroundColor: isRoot ? 'rgba(255,255,255,0.2)' : '#f3f4f6' }}
          title="Files this imports"
        >
          → {imports.length}
        </span>
        {isEntryPoint && (
          <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-700" title="Entry point">
            entry
          </span>
        )}
        {isBarrel && (
          <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700" title="Barrel/index file">
            barrel
          </span>
        )}
        {hasCircularDep && (
          <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700" title="Circular dependency">
            circular
          </span>
        )}
      </div>
      {/* Phase 2: expansion mode expand/collapse button */}
      {onExpand !== undefined && imports.length > 0 && (
        <button
          onClick={e => { e.stopPropagation(); onExpand(id); }}
          className={`mt-1.5 w-full text-left text-xs px-1.5 py-0.5 rounded transition-colors ${
            isExpanded
              ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              : 'bg-gray-50 text-gray-500 hover:bg-gray-100'
          }`}
          title={isExpanded ? 'Collapse imports' : `Expand ${imports.length} imports`}
        >
          {isExpanded ? '▼' : '▶'} {imports.length} import{imports.length !== 1 ? 's' : ''}
        </button>
      )}
    </div>
  );
}

export const importGraphNodeTypes = {
  importGraphNode: ImportGraphNodeComponent,
};
